import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/use-auth';
import { Input, Label, Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui';
import { MerchantSelect } from '@/components/features/merchants/merchant-select';
import { DateRangePicker, type DateRange } from '@/components/shared/date-range-picker';
import type { ListAdminTransactionsParams, TransactionStatus, GatewayMode } from '@/types/admin-transaction';

const ALL_STATUSES: TransactionStatus[] = [
  'pending', 'awaiting_identification', 'identified',
  'awaiting_consent', 'setup_complete', 'charged',
  'renewed', 'failed', 'cancelled', 'expired',
];

const FLOW_TYPES = ['WEB_TAN', 'WAP_CLICK'] as const;
const GATEWAY_MODES: GatewayMode[] = ['live', 'mock'];

interface TransactionFiltersProps {
  /** Server-seitige Filter (resetten Cursor) */
  filters: ListAdminTransactionsParams;
  onFiltersChange: (filters: ListAdminTransactionsParams) => void;
  /** Client-seitige Filter (kein Cursor-Reset) */
  amountMin?: string;
  amountMax?: string;
  onAmountMinChange: (value: string) => void;
  onAmountMaxChange: (value: string) => void;
  dateFrom?: Date;
  dateTo?: Date;
  onDateRangeChange: (range: DateRange) => void;
}

export function TransactionFilters({
  filters,
  onFiltersChange,
  amountMin,
  amountMax,
  onAmountMinChange,
  onAmountMaxChange,
  dateFrom,
  dateTo,
  onDateRangeChange,
}: TransactionFiltersProps) {
  const { t } = useTranslation('transactions');
  const { user } = useAuth();
  const isPlatformAdmin = user?.role === 'platform_admin';

  const handleStatusChange = (value: string) => {
    onFiltersChange({
      ...filters,
      status: value === 'all' ? undefined : (value as TransactionStatus),
      cursor: undefined,
    });
  };

  const handleMerchantChange = (value: string | undefined) => {
    onFiltersChange({
      ...filters,
      merchantId: value,
      cursor: undefined,
    });
  };

  const handleServiceChange = (value: string) => {
    onFiltersChange({
      ...filters,
      serviceId: value || undefined,
      cursor: undefined,
    });
  };

  const handleFlowTypeChange = (value: string) => {
    onFiltersChange({
      ...filters,
      flowType: value === 'all' ? undefined : (value as 'WEB_TAN' | 'WAP_CLICK'),
      cursor: undefined,
    });
  };

  const handleGatewayModeChange = (value: string) => {
    onFiltersChange({
      ...filters,
      gatewayMode: value === 'all' ? undefined : (value as GatewayMode),
      cursor: undefined,
    });
  };

  return (
    <div className="mb-4 space-y-3">
      {/* Zeile 1: bestehende Server-Filter */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={filters.status ?? 'all'} onValueChange={handleStatusChange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder={t('list.filters.status')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('list.filters.allStatuses')}</SelectItem>
            {ALL_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {t(`statuses.${s}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {isPlatformAdmin && (
          <MerchantSelect
            value={filters.merchantId}
            onChange={handleMerchantChange}
            className="w-[220px]"
          />
        )}

        <Input
          placeholder={t('list.filters.service')}
          value={filters.serviceId ?? ''}
          onChange={(e) => handleServiceChange(e.target.value)}
          className="w-[180px]"
        />
      </div>

      {/* Zeile 2: neue Filter (Server: FlowType/Mode, Client: Betrag/Datum) */}
      <div className="flex flex-wrap items-end gap-3">
        <Select value={filters.flowType ?? 'all'} onValueChange={handleFlowTypeChange}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder={t('list.filters.flowType', 'Flow Type')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('list.filters.allFlowTypes', 'Alle Flows')}</SelectItem>
            {FLOW_TYPES.map((ft) => (
              <SelectItem key={ft} value={ft}>{ft}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filters.gatewayMode ?? 'all'} onValueChange={handleGatewayModeChange}>
          <SelectTrigger className="w-[130px]">
            <SelectValue placeholder={t('list.filters.mode', 'Modus')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('list.filters.allModes', 'Alle Modi')}</SelectItem>
            {GATEWAY_MODES.map((gm) => (
              <SelectItem key={gm} value={gm}>
                {gm.toUpperCase()}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-end gap-1.5">
          <div>
            <Label className="text-xs text-muted-foreground">{t('list.filters.amountMin', 'Min EUR')}</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              value={amountMin ?? ''}
              onChange={(e) => onAmountMinChange(e.target.value)}
              className="w-[100px]"
            />
          </div>
          <span className="pb-2 text-muted-foreground">–</span>
          <div>
            <Label className="text-xs text-muted-foreground">{t('list.filters.amountMax', 'Max EUR')}</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder="999.99"
              value={amountMax ?? ''}
              onChange={(e) => onAmountMaxChange(e.target.value)}
              className="w-[100px]"
            />
          </div>
        </div>

        <DateRangePicker
          from={dateFrom}
          to={dateTo}
          onChange={onDateRangeChange}
        />
      </div>
    </div>
  );
}
